import type { Attributes, BackgroundId, ConsumableId, ResourceId, Rarity } from '../types';

export interface StartingItem {
  templateId: string;
  rarity: Rarity;
}

export interface BackgroundDef {
  id: BackgroundId;
  name: string;
  bonus: Partial<Attributes>;
  /** Flavour line under the numbers on the creation screen. */
  line: string;
  /** 10. 10. 2026 (owner): one sentence on how it plays (its attributes and where its skill tree starts). */
  style: string;
  weapon: StartingItem;
  extraItems: StartingItem[];
  startStacks: Partial<Record<ResourceId | ConsumableId, number>>;
  /** Passive perks of the background. */
  perks: {
    loot?: number; // +10% loot
    craftSpeed?: number; // 0.2 = 20% faster crafting
    stealth?: number; // +5% stealth
    detect?: number; // +5% detect
  };
  panel: string; // card gradient from the onboarding design
  icon: string; // 24x24 line icon path
}

export const BACKGROUNDS_DEF: Record<BackgroundId, BackgroundDef> = {
  soldier: {
    id: 'soldier',
    name: 'Soldier',
    bonus: { str: 3, vit: 2 },
    line: 'Knows which end goes bang.',
    style: 'Fights head-on and takes the hits: strong up close and hard to put down.',
    weapon: { templateId: 'pipe_shotgun', rarity: 'common' },
    extraItems: [],
    // 7. 10. 2026 (owner, Ammo per shot): 60 rounds, about the 20 fights of before (a fight takes 5–6 shots in the Forest).
    startStacks: { ammo: 60 },
    perks: {},
    panel: 'linear-gradient(180deg,#2E3326,#1E2119)',
    icon: 'M2 12 H16 V9 H21 V12 M16 12 L14 16 H11 L12 12 M5 12 V15',
  },
  medic: {
    id: 'medic',
    name: 'Medic',
    bonus: { vit: 3, int: 2 },
    line: 'Keeps people breathing. Including yourself.',
    style: 'Stays alive and keeps going: more health, Medkits, and supplies that last longer.',
    weapon: { templateId: 'pipe_wrench', rarity: 'common' },
    extraItems: [{ templateId: 'medic_satchel', rarity: 'common' }],
    startStacks: { medkit: 2 },
    perks: {},
    panel: 'linear-gradient(180deg,#3A2420,#221715)',
    icon: 'M12 5 V19 M5 12 H19',
  },
  engineer: {
    id: 'engineer',
    name: 'Engineer',
    bonus: { int: 3, per: 2 },
    line: 'Fixes what others throw out.',
    style: 'Builds and mends: faster, cheaper crafting and repairs, then a steady aim.',
    weapon: { templateId: 'pipe_wrench', rarity: 'common' },
    extraItems: [{ templateId: 'work_gloves', rarity: 'common' }],
    startStacks: { repair_kit: 1, metal: 10 },
    perks: { craftSpeed: 0.2 },
    panel: 'linear-gradient(180deg,#22313A,#161F24)',
    icon: 'M14 6 A4 4 0 1 0 18 10 L21 13 L13 21 L10 18 L14 14 Z M4 20 L10 14',
  },
  hunter: {
    id: 'hunter',
    name: 'Hunter',
    bonus: { per: 3, agi: 2 },
    line: 'Quiet feet. Patient hands.',
    style: 'Strikes from range and from hiding: a bow, sharp eyes and quiet feet.',
    weapon: { templateId: 'recurve_bow', rarity: 'common' },
    extraItems: [],
    startStacks: {},
    perks: { stealth: 0.05, detect: 0.05 },
    panel: 'linear-gradient(180deg,#2E2A1E,#1E1B14)',
    icon: 'M5 19 C 3 12 12 3 19 5 M5 19 L19 5 M9 15 L4 10 M15 9 L10 4',
  },
  scavenger: {
    id: 'scavenger',
    name: 'Scavenger',
    bonus: { agi: 3, per: 2 },
    line: 'Finds things in places already searched.',
    style: 'Gets in, finds more and gets out: quick, hard to catch, more loot from every search.',
    weapon: { templateId: 'pipe_wrench', rarity: 'common' },
    extraItems: [],
    startStacks: { scrap: 10 },
    perks: { loot: 0.1 },
    panel: 'linear-gradient(180deg,#352B1E,#221B13)',
    icon: 'M5 6 H19 V21 H5 Z M9 6 V4 H15 V6 M8 13 H16',
  },
};

/** Gear and stacks every new survivor gets on top of the background kit. */
export const STARTING_KIT = {
  items: [
    { templateId: 'patched_hoodie', rarity: 'common' as Rarity, slot: 'body' as const },
    { templateId: 'worn_sneakers', rarity: 'common' as Rarity, slot: 'boots' as const },
    { templateId: 'school_backpack', rarity: 'common' as Rarity, slot: 'backpack' as const },
  ],
  // Krug 5 (K12.5): 6 Food and 2 Rations (was 12 Food): meals replace the Food a run used to eat.
  stacks: { food: 6, ration: 2, scrap: 20, cloth: 6, metal: 6, wood: 4, bandage: 2 } as Partial<Record<ResourceId | ConsumableId, number>>,
};
